import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import SuspenseLoader from '../../components/SuspenseLoader';
import { useAuth } from '../../features/authentication';
import pages from '../../router/routes';
import { getCurrentUser } from '../../services/tanstackQuery/queries/users/users.service';
import { useCompanyStore } from '../../store/company/companyStore';
import { useUserAuthStore } from '../../store/userAuth/userAuthStore';

interface CompanyGuardProps {
  children: JSX.Element;
}

export const CompanyGuard = ({ children }: CompanyGuardProps) => {
  const { isAuthenticated, loading } = useAuth();
  const dbUser = useUserAuthStore((state) => state.dbUser);
  const setDbUser = useUserAuthStore((state) => state.setDbUser);
  const setUserStatus = useUserAuthStore((state) => state.setUserStatus);

  const activeCompany = useCompanyStore((state) => state.activeCompany);
  const setActiveCompany = useCompanyStore((state) => state.setActiveCompany);
  const setCompanies = useCompanyStore((state) => state.setCompanies);

  const preferredCompanyIdRef = useRef<string | null>(null);
  const activeCompanyId = activeCompany?.company?.id ?? null;

  useEffect(() => {
    if (activeCompanyId) {
      preferredCompanyIdRef.current = activeCompanyId;
    }
  }, [activeCompanyId]);

  const {
    data,
    isLoading: isFetchingUser,
    isError,
  } = useQuery({
    queryKey: ['current-user'],
    queryFn: getCurrentUser,
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
    refetchInterval: 180000,
    staleTime: 120000,
    retry: 1,
  });

  useEffect(() => {
    if (!data?.data) {
      return;
    }

    const userRecord = data.data;
    const userRoles = userRecord.userRoles ?? [];

    setDbUser(userRecord);
    setUserStatus('authenticated');
    setCompanies(userRoles);

    if (userRoles.length === 0) {
      setActiveCompany(null);
      return;
    }

    const storedPreferredId = preferredCompanyIdRef.current;
    const storedMatch = storedPreferredId
      ? userRoles.find((role) => role.company.id === storedPreferredId)
      : undefined;
    const currentMatch = activeCompanyId
      ? userRoles.find((role) => role.company.id === activeCompanyId)
      : undefined;

    const nextCompany = storedMatch ?? currentMatch ?? userRoles[0];

    if (!activeCompanyId || nextCompany.company.id !== activeCompanyId) {
      setActiveCompany(nextCompany);
    }
  }, [
    data,
    setDbUser,
    setUserStatus,
    setCompanies,
    setActiveCompany,
    activeCompanyId,
  ]);

  if (!isAuthenticated && !loading) {
    return <Navigate to={pages.auth.login.path} replace />;
  }

  if ((isFetchingUser || loading) && !dbUser) {
    return <SuspenseLoader />;
  }

  if (isError) {
    return <Navigate to={pages.auth.login.path} replace />;
  }

  if (!dbUser) {
    return <SuspenseLoader />;
  }

  if (!activeCompany) {
    return <Navigate to={pages.status.statusFailure.path} replace />;
  }

  return <>{children}</>;
};

export default CompanyGuard;
